import type { Auth, Transaction } from 'oscar-typedef'
import { EthereumPrepareData } from '../prepareData/EthereumPrepareData'
import type { EthereumSendExtraData } from '../sendExtraData/EthereumSendExtraData'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'
import type { Account, ITransactionExecutor, EncUVD } from '@/types'

export class EthereumCoinTransactionExecutor implements ITransactionExecutor {
  private account: Account

  constructor(params: { account: Account }) {
    this.account = params.account
  }

  private get gasDecimal() {
    return (this.account.coin?.property?.gasDecimal as number) ?? 9
  }

  async getPrepareData(params: {
    to: string
    amount: string
  }): Promise<EthereumPrepareData> {
    const data =
      await WepinBackendAPI.getInstance().transaction.getTransactionPrepareData(
        {
          accountId: this.account.id,
          to: params.to,
          amount: params.amount,
        },
      )
    return new EthereumPrepareData({
      gasPrice: {
        high: data.detail?.gasPrice.high.toString() ?? '0',
        medium: data.detail?.gasPrice.medium.toString() ?? '0',
        low: data.detail?.gasPrice.low.toString() ?? '0',
      },
      gasLimit: data.detail?.gasLimit ?? 0,
      nonce: data.detail?.nonce ?? 0,
      decimal: this.account.coin?.decimals ?? 0,
    })
  }

  async sign(params: {
    to: string
    amount: string
    encUVD: EncUVD
    extraData: EthereumSendExtraData
    otpCode?: Auth.VerifyCode.Request.Body
  }): Promise<Transaction.Sign.Response.Body> {
    const result = await WepinBackendAPI.getInstance().transaction.sign({
      type: 'transaction',
      walletId: this.account.walletId,
      accountId: this.account.id,
      pin: params.encUVD,
      txData: {
        gasLimit: params.extraData.gasLimit,
        gasPrice: params.extraData.gasPrice.amount.shiftedBy(-this.gasDecimal),
        to: params.to,
        nonce: params.extraData.nonce,
        amount: params.amount,
        data: '0x',
      },
      otpCode: params.otpCode,
    })
    return result
  }

  async broadcast(params: {
    signResponse: Transaction.Sign.Response.Body
  }): Promise<Transaction.BroadcastTransaction.Response.Body> {
    return await WepinBackendAPI.getInstance().transaction.broadcastTransaction(
      {
        walletId: this.account.walletId,
        accountId: this.account.id,
        transaction: params.signResponse.transaction,
        signatureResult: params.signResponse.signatureResult,
      },
    )
  }
}
